const API_BASE = import.meta.env.VITE_API_URL || "/api"

class ApiClient {
  constructor() {
    this.token = localStorage.getItem("token")
  }

  setToken(token) {
    this.token = token
    localStorage.setItem("token", token)
  }

  clearToken() {
    this.token = null
    localStorage.removeItem("token")
  }

  async request(endpoint, options = {}) {
    const { method = "GET", body, headers = {} } = options

    const config = {
      method,
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
    }

    if (this.token) {
      config.headers.Authorization = `Bearer ${this.token}`
    }

    if (body) {
      config.body = JSON.stringify(body)
    }

    const response = await fetch(`${API_BASE}${endpoint}`, config)
    const data = await response.json()

    if (!response.ok) {
      if (response.status === 401) {
        this.clearToken()
        window.location.href = "/login"
      }
      throw new Error(typeof data.error === 'string' ? data.error : data.error?.message || "Request failed")
    }

    return data
  }

  get(endpoint) {
    return this.request(endpoint)
  }

  post(endpoint, body) {
    return this.request(endpoint, { method: "POST", body })
  }

  put(endpoint, body) {
    return this.request(endpoint, { method: "PUT", body })
  }

  delete(endpoint) {
    return this.request(endpoint, { method: "DELETE" })
  }

  // Auth
  login(username, password) {
    return this.post("/auth/login", { username, password })
  }

  me() {
    return this.get("/auth/me")
  }

  // Users
  getUsers(params = {}) {
    const query = new URLSearchParams(params).toString()
    return this.get(`/users?${query}`)
  }

  getUser(id) {
    return this.get(`/users/${id}`)
  }

  createUser(data) {
    return this.post("/users", data)
  }

  updateUser(id, data) {
    return this.put(`/users/${id}`, data)
  }

  deleteUser(id) {
    return this.delete(`/users/${id}`)
  }

  // Configs
  getConfigs(params = {}) {
    const query = new URLSearchParams(params).toString()
    return this.get(`/configs?${query}`)
  }

  createConfig(data) {
    return this.post("/configs", data)
  }

  updateConfig(id, data) {
    return this.put(`/configs/${id}`, data)
  }

  deleteConfig(id) {
    return this.delete(`/configs/${id}`)
  }

  // Settings
  getSettings() {
    return this.get("/settings")
  }

  updateSetting(data) {
    return this.post("/settings", data)
  }

  batchUpdateSettings(settings) {
    return this.post("/settings/batch", { settings })
  }

  // Pricing
  getPlans() {
    return this.get("/pricing/plans")
  }

  createPlan(data) {
    return this.post("/pricing/plans", data)
  }

  updatePlan(id, data) {
    return this.put(`/pricing/plans/${id}`, data)
  }

  deletePlan(id) {
    return this.delete(`/pricing/plans/${id}`)
  }

  getRevenue() {
    return this.get("/pricing/revenue")
  }

  // Features
  getFeatures() {
    return this.get("/features")
  }

  toggleFeature(name) {
    return this.post(`/features/${name}/toggle`)
  }

  // Panels
  getPanels() {
    return this.get("/panels")
  }

  createPanel(data) {
    return this.post("/panels", data)
  }

  updatePanel(id, data) {
    return this.put(`/panels/${id}`, data)
  }

  deletePanel(id) {
    return this.delete(`/panels/${id}`)
  }

  checkPanelStatus(id) {
    return this.post(`/panels/${id}/status`)
  }

  syncPanelUsers(id) {
    return this.post(`/panels/${id}/sync`)
  }

  // Themes
  getThemes() {
    return this.get("/themes")
  }

  getActiveTheme() {
    return this.get("/themes/active")
  }

  activateTheme(name) {
    return this.post(`/themes/${name}/activate`)
  }

  // Permissions
  getRoles() {
    return this.get("/permissions/roles")
  }

  createRole(data) {
    return this.post("/permissions/roles", data)
  }

  // Audit
  getAuditLogs(params = {}) {
    const query = new URLSearchParams(params).toString()
    return this.get(`/audit?${query}`)
  }

  getAuditStats() {
    return this.get("/audit/stats")
  }

  // Versioning
  getVersions(params = {}) {
    const query = new URLSearchParams(params).toString()
    return this.get(`/versions?${query}`)
  }

  restoreVersion(id) {
    return this.post(`/versions/${id}/restore`)
  }

  // Backup
  getBackups() {
    return this.get("/backups")
  }

  createBackup(data) {
    return this.post("/backups", data)
  }

  restoreBackup(id) {
    return this.post(`/backups/${id}/restore`)
  }

  // Health
  getHealthDashboard() {
    return this.get("/health/dashboard")
  }

  // Secrets
  getSecrets() {
    return this.get("/secrets")
  }

  createSecret(data) {
    return this.post("/secrets", data)
  }

  // Queue
  getQueues() {
    return this.get("/queues")
  }

  // Jobs
  getJobs() {
    return this.get("/jobs")
  }

  runJob(id) {
    return this.post(`/jobs/${id}/run`)
  }

  // Plugins
  getPlugins() {
    return this.get("/plugins")
  }

  togglePlugin(id) {
    return this.post(`/plugins/${id}/toggle`)
  }

  // Events
  getEvents(params = {}) {
    const query = new URLSearchParams(params).toString()
    return this.get(`/events?${query}`)
  }

  getEventStats() {
    return this.get("/events/stats")
  }

  // Cache
  getCacheStats() {
    return this.get("/cache/stats")
  }

  warmupCache(module) {
    return this.post("/cache/warmup", { module })
  }

  invalidateCache(data) {
    return this.post("/cache/invalidate", data)
  }

  // Monitoring
  getMonitoringDashboard() {
    return this.get("/monitoring")
  }

  getRealtimeStats() {
    return this.get("/monitoring/realtime")
  }

  // Workflows
  getWorkflows() {
    return this.get("/workflows")
  }

  executeWorkflow(id, input) {
    return this.post(`/workflows/${id}/execute`, { input })
  }
}

export const api = new ApiClient()
export default api
